import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosDownwardFill from '@iconify/icons-eva/arrow-ios-downward-fill';
// material
import { styled } from '@mui/material/styles';
import { Box, List, Collapse, ListItemText, ListItemIcon, ListSubheader, ListItemButton } from '@mui/material';

// ----------------------------------------------------------------------

const ListSubheaderStyle = styled((props) => <ListSubheader disableSticky disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.overline,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2.5),
    color: '#51CBFF'
  })
);

const ListItemStyle = styled(ListItemButton)(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  color: '#0E38AF',
  '&:before': {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: 'none',
    position: 'absolute',
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ----------------------------------------------------------------------

/** Every path in the sidebar, so an item can tell whether a more specific one matches. */
function collectNavPaths(navConfig) {
  const paths = [];
  navConfig.forEach(({ items = [] }) =>
    items.forEach((item) => {
      if (item.path) paths.push(item.path);
      (item.children ?? []).forEach((child) => child.path && paths.push(child.path));
    })
  );
  return paths;
}

const segmentCount = (path) => path.split('/').filter(Boolean).length;

/**
 * Highlight only the most specific menu entry for the current route.
 *
 * Matching stays prefix-based (`end: false`) so a parent entry keeps its highlight on
 * its own detail routes (e.g. Audit Campaigns on `/audit-campaigns/:id`). Without the
 * specificity check a nested sibling would light up two entries at once: Prompt
 * Templates lives at `/audit-campaigns/prompts`, which also prefix-matches Audit
 * Campaigns.
 */
function isMostSpecificMatch(path, pathname, allPaths) {
  if (!path || !matchPath({ path, end: false }, pathname)) return false;
  return !allPaths.some(
    (candidate) =>
      candidate !== path &&
      segmentCount(candidate) > segmentCount(path) &&
      !!matchPath({ path: candidate, end: false }, pathname)
  );
}

NavItem.propTypes = {
  isShow: PropTypes.bool,
  item: PropTypes.object,
  allPaths: PropTypes.array
};

function NavItem({ item, isShow, allPaths = [] }) {
  const { pathname } = useLocation();
  const { title, path, icon, info, children } = item;
  const isActiveRoot = isMostSpecificMatch(path, pathname, allPaths);
  // Collapse state uses the loose match: a parent must expand while any of its
  // child routes is open, even when the highlight belongs to the child.
  const containsCurrentRoute = path ? !!matchPath({ path, end: false }, pathname) : false;

  const [open, setOpen] = useState(containsCurrentRoute);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: '#fff',
    fontWeight: 'fontWeightMedium',
    bgcolor: '#1B2B65'
    // '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: '#fff',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>

          {isShow && (
            <>
              <ListItemText disableTypography primary={title} />
              {info && info}
              <Box
                component={Icon}
                icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
                sx={{ width: 16, height: 16, ml: 1 }}
              />
            </>
          )}
        </ListItemStyle>

        {isShow && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {children.map((item) => {
                const { title, path } = item;
                const isActiveSub = isMostSpecificMatch(path, pathname, allPaths);

                return (
                  <ListItemStyle
                    key={title}
                    component={RouterLink}
                    to={path}
                    sx={{
                      ...(isActiveSub && activeSubStyle)
                    }}
                  >
                    <ListItemIconStyle>
                      <Box
                        component="span"
                        sx={{
                          width: 4,
                          height: 4,
                          display: 'flex',
                          borderRadius: '50%',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'text.disabled',
                          transition: (theme) => theme.transitions.create('transform'),
                          ...(isActiveSub && {
                            transform: 'scale(2)',
                            bgcolor: 'primary.main'
                          })
                        }}
                      />
                    </ListItemIconStyle>
                    <ListItemText disableTypography primary={title} />
                  </ListItemStyle>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle)
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      {isShow && (
        <>
          <ListItemText disableTypography primary={title} />
          {info && info}
        </>
      )}
    </ListItemStyle>
  );
}

NavSection.propTypes = {
  isShow: PropTypes.bool,
  navConfig: PropTypes.array
};

export default function NavSection({ navConfig, isShow = true, ...other }) {
  const allPaths = useMemo(() => collectNavPaths(navConfig), [navConfig]);

  return (
    <Box {...other}>
      {navConfig.map((list) => {
        const { subheader, items } = list;
        return (
          <List key={subheader} disablePadding>
            {isShow && <ListSubheaderStyle>{subheader}</ListSubheaderStyle>}
            {items.map((item) => (
              <NavItem key={item.title} item={item} isShow={isShow} allPaths={allPaths} />
            ))}
          </List>
        );
      })}
    </Box>
  );
}
